"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import {
  GET_APP_AFTER_PICKS_DELAY_MS,
  GET_APP_LANDING_DELAY_MS,
  GET_APP_SHEET_STORAGE_KEY,
  appStorePlatform,
  isGetAppLandingPath,
  parseGetAppLastShown,
  shouldAutoShowGetAppSheet,
  storeUrlForPlatform,
  useDesktopPwaInstall,
  useDevicePlatform,
  type AppStorePlatform,
  type GetAppMoment,
  type PwaPlatform,
} from "@award-party/shared";
import { capturePostHogEvent } from "@/lib/posthog-capture";
import { useAnalyticsConsent } from "@/lib/use-analytics-consent";
import { DesktopInstallDialog } from "./desktop-install-dialog";
import { GetAppSheet } from "./get-app-sheet";

interface GetAppContextValue {
  platform: PwaPlatform;
  isNativeShell: boolean;
  store: AppStorePlatform | null;
  iosUrl: string | null;
  androidUrl: string | null;
  /** Show the store entry points on a phone whose listing is live. */
  canGetApp: boolean;
  /** A desktop browser can install the site as an app. There is no desktop store app. */
  canInstallOnDesktop: boolean;
  openGetApp: () => void;
  openDesktopInstall: () => void;
  offerAfterPicks: () => void;
  trackStoreClick: (store: AppStorePlatform, surface: string) => void;
}

/**
 * Used when a consumer renders without a provider above it (for example a
 * dropped Clerk session on the vote page). The entry points stay inert
 * instead of throwing.
 */
const FALLBACK_GET_APP: GetAppContextValue = {
  platform: "unknown",
  isNativeShell: false,
  store: null,
  iosUrl: null,
  androidUrl: null,
  canGetApp: false,
  canInstallOnDesktop: false,
  openGetApp: () => {},
  openDesktopInstall: () => {},
  offerAfterPicks: () => {},
  trackStoreClick: () => {},
};

const GetAppContext = createContext<GetAppContextValue | null>(null);

function readLastShown(): number | null {
  try {
    return parseGetAppLastShown(localStorage.getItem(GET_APP_SHEET_STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeLastShown(now: number) {
  try {
    localStorage.setItem(GET_APP_SHEET_STORAGE_KEY, String(now));
  } catch {
    // Private mode can block storage; the sheet just may show again.
  }
}

export function GetAppProvider({
  children,
  iosUrl,
  androidUrl,
}: {
  children: ReactNode;
  iosUrl: string | null;
  androidUrl: string | null;
}) {
  const pathname = usePathname();
  const { platform, isNativeShell } = useDevicePlatform();
  const { shouldShowBanner: cookieBannerVisible, isReady: consentReady } =
    useAnalyticsConsent();
  const [open, setOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [moment, setMoment] = useState<GetAppMoment>("landing");
  const landingCheckedRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const {
    canInstall: canInstallOnDesktop,
    guide: desktopGuide,
    canNativeInstall,
    promptNativeInstall,
  } = useDesktopPwaInstall();

  const store = appStorePlatform(platform);
  const phoneStoreUrl = storeUrlForPlatform(platform, { iosUrl, androidUrl });
  const canGetApp = !isNativeShell && store !== null && Boolean(phoneStoreUrl);

  const show = useCallback(
    (nextMoment: GetAppMoment) => {
      setMoment(nextMoment);
      setOpen(true);
      writeLastShown(Date.now());
      capturePostHogEvent("get_app_sheet_viewed", {
        moment: nextMoment,
        store: store ?? "desktop",
      });
    },
    [store],
  );

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  useEffect(() => {
    if (
      landingCheckedRef.current ||
      platform === "unknown" ||
      !consentReady ||
      cookieBannerVisible
    ) {
      return;
    }
    landingCheckedRef.current = true;

    if (
      !isGetAppLandingPath(pathname) ||
      !shouldAutoShowGetAppSheet({
        platform,
        isNativeShell,
        iosUrl,
        androidUrl,
        lastShownAt: readLastShown(),
        now: Date.now(),
        moment: "landing",
      })
    ) {
      return;
    }

    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      show("landing");
    }, GET_APP_LANDING_DELAY_MS);
  }, [
    androidUrl,
    consentReady,
    cookieBannerVisible,
    iosUrl,
    isNativeShell,
    pathname,
    platform,
    show,
  ]);

  const offerAfterPicks = useCallback(() => {
    if (
      !shouldAutoShowGetAppSheet({
        platform,
        isNativeShell,
        iosUrl,
        androidUrl,
        lastShownAt: readLastShown(),
        now: Date.now(),
        moment: "picks_complete",
      })
    ) {
      return;
    }

    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      show("picks_complete");
    }, GET_APP_AFTER_PICKS_DELAY_MS);
  }, [androidUrl, clearTimer, iosUrl, isNativeShell, platform, show]);

  const openGetApp = useCallback(() => {
    clearTimer();
    show("menu");
  }, [clearTimer, show]);

  const openDesktopInstall = useCallback(() => {
    clearTimer();
    setDesktopOpen(true);
  }, [clearTimer]);

  const handleNativeInstall = useCallback(() => {
    void promptNativeInstall().then((outcome) => {
      if (outcome === "accepted") {
        setDesktopOpen(false);
      }
    });
  }, [promptNativeInstall]);

  const trackStoreClick = useCallback(
    (clickedStore: AppStorePlatform, surface: string) => {
      capturePostHogEvent("store_download_clicked", {
        store: clickedStore,
        surface,
      });
    },
    [],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        capturePostHogEvent("get_app_sheet_dismissed", { moment });
      }
      setOpen(nextOpen);
    },
    [moment],
  );

  const value = useMemo(
    () => ({
      platform,
      isNativeShell,
      store,
      iosUrl,
      androidUrl,
      canGetApp,
      canInstallOnDesktop,
      openGetApp,
      openDesktopInstall,
      offerAfterPicks,
      trackStoreClick,
    }),
    [
      androidUrl,
      canGetApp,
      canInstallOnDesktop,
      iosUrl,
      isNativeShell,
      offerAfterPicks,
      openDesktopInstall,
      openGetApp,
      platform,
      store,
      trackStoreClick,
    ],
  );

  return (
    <GetAppContext.Provider value={value}>
      {children}
      {canGetApp ? (
        <GetAppSheet
          open={open}
          onOpenChange={handleOpenChange}
          moment={moment}
          store={store}
          iosUrl={iosUrl}
          androidUrl={androidUrl}
          onStoreClick={(clickedStore) => {
            trackStoreClick(clickedStore, `sheet_${moment}`);
            setOpen(false);
          }}
        />
      ) : null}
      {canInstallOnDesktop && desktopGuide ? (
        <DesktopInstallDialog
          open={desktopOpen}
          onOpenChange={setDesktopOpen}
          guide={desktopGuide}
          canNativeInstall={canNativeInstall}
          onNativeInstall={handleNativeInstall}
        />
      ) : null}
    </GetAppContext.Provider>
  );
}

export function useGetApp(): GetAppContextValue {
  return useContext(GetAppContext) ?? FALLBACK_GET_APP;
}
